import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { useTranslation } from 'react-i18next';
import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { InputLabel } from '@/components/ui/InputLabel';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchReplayConfiguration,
  updateReplayConfiguration,
} from '@/features/webinar/webinarThunks';
import { cn } from '@/lib/utils';
import Loader from '@/components/ui/Loader';
import { Button } from '@/components/ui/button';
import { toast } from 'react-toastify';
import { mapErrorMessage } from '@/lib/mapApiError';
import { normalizeAxiosError } from '@/http/errors';
import type { AppError } from '@/features/auth/types';

type FormValues = {
  active: boolean;
  noLimitDate: boolean;
  limitDate: string | null;
  validityDays: number;
  replayType: 'REPLICA' | 'EXTERNAL_LINK' | null;
  externalLink: string | null;
  displayVideoCommands: boolean;
  allowComments: boolean;
};

interface ReplaySectionProps {
  currentWebinarId?: number | null;
  isEdit?: boolean;
  onSaveSuccess?: () => void;
}
export default function ReplaySection({
  currentWebinarId,
  isEdit,
  onSaveSuccess,
}: ReplaySectionProps) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const { currentReplayConfiguration, currentWebinar } = useAppSelector(
    (state: RootState) => state.webinar,
  );
  const isEvergreen = currentWebinar?.type === 'EVERGREEN';
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarId = webinarIdParam || currentWebinarId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string[]>(
    isEdit ? [] : isEvergreen ? [] : ['item-1'],
  );
  const [typeModified, setTypeModified] = useState(false);
  const [accessModified, setAccessModified] = useState(false);
  const [validityModified, setValidityModified] = useState(false);

  const schema = useMemo(
    () =>
      z
        .object({
          active: z.boolean(),
          noLimitDate: z.boolean(),
          limitDate: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/, {
              message: t('replaySection.validation.invalidDateFormat'),
            })
            .nullable(),
          validityDays: z.number().int().min(0),
          replayType: z
            .union([z.enum(['REPLICA', 'EXTERNAL_LINK']), z.null(), z.undefined(), z.literal('')])
            .transform((val) => (val === '' || val === undefined ? null : val)),
          externalLink: z
            .string()
            .nullable()
            .transform((val) => (val === undefined ? null : val)),
          displayVideoCommands: z.boolean(),
          allowComments: z.boolean(),
        })
        .refine((data) => !data.active || data.replayType != null, {
          message: t('replaySection.validation.replayTypeRequired'),
          path: ['replayType'],
        })
        .refine(
          (data) =>
            !data.active ||
            data.replayType !== 'EXTERNAL_LINK' ||
            (data.externalLink !== null && data.externalLink.trim() !== ''),
          {
            message: t('replaySection.validation.externalLinkRequired'),
            path: ['externalLink'],
          },
        )
        .refine(
          (data) => {
            if (!data.active || data.replayType !== 'EXTERNAL_LINK' || !data.externalLink?.trim()) {
              return true;
            }
            const link = data.externalLink.trim();
            return /^https:\/\//i.test(link) || /^http:\/\//i.test(link);
          },
          {
            message: t('replaySection.validation.externalLinkFormat'),
            path: ['externalLink'],
          },
        ),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues>,
    defaultValues: {
      active: false,
      noLimitDate: true,
      limitDate: null,
      validityDays: 0,
      replayType: null,
      externalLink: null,
      displayVideoCommands: true,
      allowComments: true,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (webinarId && workspaceId) {
      const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
      if (!isNaN(id)) {
        dispatch(fetchReplayConfiguration({ workspaceId, webinarId: id }));
      }
    }
  }, [webinarId, workspaceId, dispatch]);

  useEffect(() => {
    if (currentReplayConfiguration) {
      form.reset({
        active: currentReplayConfiguration.active,
        noLimitDate: currentReplayConfiguration.noLimitDate,
        limitDate: currentReplayConfiguration.limitDate ?? null,
        validityDays: currentReplayConfiguration.validityDays ?? 0,
        replayType: currentReplayConfiguration.replayType ?? null,
        externalLink: currentReplayConfiguration.externalLink ?? null,
        displayVideoCommands: currentReplayConfiguration.displayVideoCommands,
        allowComments: currentReplayConfiguration.allowComments,
      });
    }
  }, [currentReplayConfiguration, form]);

  useEffect(() => {
    if (isEdit) {
      setAccordionValue([]);
    } else {
      setAccordionValue(isEvergreen ? [] : ['item-1']);
    }
  }, [isEdit, isEvergreen]);

  const handleSubmitBase = async (
    shouldNavigate: boolean = false,
    modifiedFlags?: {
      typeModified?: boolean;
      accessModified?: boolean;
      validityModified?: boolean;
    },
  ): Promise<boolean> => {
    if (!webinarId || !workspaceId) {
      return false;
    }

    const isValid = await form.trigger();
    if (!isValid) {
      return false;
    }

    const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
    if (isNaN(id)) {
      return false;
    }

    try {
      setIsSubmitting(true);
      const data = form.getValues();
      const externalLink =
        data.replayType === 'EXTERNAL_LINK' ? (data.externalLink?.trim() ?? null) : null;

      await dispatch(
        updateReplayConfiguration({
          workspaceId,
          webinarId: id,
          input: {
            active: data.active,
            noLimitDate: data.noLimitDate,
            limitDate: data.noLimitDate ? null : (data.limitDate ?? null),
            validityDays: data.validityDays,
            replayType: data.replayType ?? null,
            externalLink,
            displayVideoCommands: data.displayVideoCommands,
            allowComments: data.allowComments,
            ...modifiedFlags,
          },
        }),
      ).unwrap();
      toast.success(t('replaySection.saveSuccess'));

      if (shouldNavigate) {
        if (isEdit && onSaveSuccess) {
          window.scrollTo({ top: 0, behavior: 'smooth' });

          onSaveSuccess();
        } else if (!isEdit) {
          navigate(`/create-webinar/integration/${id}`);
        }
      }
      return true;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);

      let appError: AppError;
      if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
        appError = error as AppError;
      } else {
        const normalized = normalizeAxiosError(error);
        appError = {
          code: normalized.code,
          message: normalized.message,
          details: normalized.details,
          status: normalized.status,
        };
      }

      const messageKey = mapErrorMessage(appError.message, appError.code, appError.status);

      const errorMessage = t(messageKey, { defaultValue: messageKey });

      let fullErrorMessage = errorMessage;

      if (appError.details && Array.isArray(appError.details) && appError.details.length > 0) {
        const detailsMessages = (
          appError.details as Array<{ message?: string; fieldName?: string }>
        )
          .map((detail) => {
            const detailMessage = detail.message
              ? t(mapErrorMessage(detail.message), { defaultValue: detail.message })
              : '';
            return detail.fieldName
              ? `${detail.fieldName}: ${detailMessage || errorMessage}`
              : detailMessage || errorMessage;
          })
          .join('\n');

        fullErrorMessage = `${errorMessage}\n${detailsMessages}`;
      }

      toast.error(fullErrorMessage);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitType = async () => {
    const success = await handleSubmitBase(false, { typeModified: true });
    if (!success) return;
    const replayType = form.getValues('replayType');
    setAccordionValue((prev) => {
      const filtered = prev.filter((item) => item !== 'item-1');
      if (replayType === 'REPLICA' && !filtered.includes('item-2')) {
        filtered.push('item-2');
      }
      return filtered;
    });
    setTypeModified(true);
  };

  const handleSubmitAccess = async () => {
    const success = await handleSubmitBase(false, { accessModified: true });
    if (!success) return;
    setAccordionValue((prev) => {
      const filtered = prev.filter((item) => item !== 'item-2');
      if (!filtered.includes('item-3')) {
        filtered.push('item-3');
      }
      return filtered;
    });
    setAccessModified(true);
  };

  const handleSubmitValidity = async () => {
    const success = await handleSubmitBase(true, { validityModified: true });
    if (!success) return;
    setValidityModified(true);
  };

  const replayType = form.watch('replayType');
  const active = form.watch('active');
  const noLimitDate = form.watch('noLimitDate');

  return (
    <div>
      <TitleLayout
        title={t('replaySection.title')}
        subtitle={t('replaySection.subtitle')}
        isEdit={isEdit}
      >
        <div className="space-y-8">
          <div className="p-4 border border-input rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <Label>{t('replaySection.recordingLabel')}</Label>
              <Controller
                name="active"
                control={form.control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      if (checked) {
                        if (!form.getValues('replayType')) {
                          form.setValue('replayType', 'REPLICA', { shouldValidate: true });
                        }
                        setAccordionValue((prev) =>
                          prev.includes('item-1') ? prev : [...prev, 'item-1'],
                        );
                      } else {
                        setAccordionValue([]);
                      }
                      if (isEdit) {
                        handleSubmitBase(false);
                      }
                    }}
                  />
                )}
              />
            </div>
            <p className="text-sm text-secondary">{t('replaySection.recordingDescription')}</p>
          </div>

          <Accordion
            type="multiple"
            value={accordionValue}
            onValueChange={setAccordionValue}
            className="space-y-8 [&>div]:after:hidden"
          >
            <AccordionItem
              value="item-1"
              className={cn(isEdit && 'accordion-page')}
              hideSeparator={isEdit}
            >
              <AccordionTrigger
                disabled={!active}
                fillForm={typeModified || currentReplayConfiguration?.typeModified}
              >
                {t('replaySection.replayType')}
              </AccordionTrigger>
              <AccordionContent>
                <Controller
                  name="replayType"
                  control={form.control}
                  render={({ field }) => (
                    <div className="space-y-2">
                      <RadioGroup
                        value={field.value ?? undefined}
                        onValueChange={(value) =>
                          field.onChange(value as 'REPLICA' | 'EXTERNAL_LINK')
                        }
                        disabled={!active}
                        className="space-x-4 flex items-center"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="REPLICA" id="replica" />
                          <Label
                            htmlFor="replica"
                            className={cn('font-normal cursor-pointer', !active && 'text-disable')}
                          >
                            {t('replaySection.replicaReplay')}
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="EXTERNAL_LINK" id="external" />
                          <Label
                            htmlFor="external"
                            className={cn('font-normal cursor-pointer', !active && 'text-disable')}
                          >
                            {t('replaySection.externalLink')}
                          </Label>
                        </div>
                      </RadioGroup>
                      {form.formState.errors.replayType && (
                        <p className="text-sm text-red-500">
                          {form.formState.errors.replayType.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                {replayType === 'EXTERNAL_LINK' && (
                  <div className="mt-4 space-y-2">
                    <Controller
                      name="externalLink"
                      control={form.control}
                      render={({ field }) => (
                        <>
                          <InputLabel
                            disabled={!active}
                            label={t('replaySection.externalLinkLabel')}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value)}
                            className={cn(form.formState.errors.externalLink && 'border-red-500')}
                          />
                          {form.formState.errors.externalLink && (
                            <p className="text-sm text-red-500">
                              {form.formState.errors.externalLink.message}
                            </p>
                          )}
                        </>
                      )}
                    />
                  </div>
                )}

                <Button
                  className="w-full mt-6 disabled:bg-gray-200 disabled:text-gray-500 "
                  onClick={handleSubmitType}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Loader />}
                  {t('save')}
                </Button>
              </AccordionContent>
            </AccordionItem>
            {replayType === 'REPLICA' && (
              <AccordionItem
                value="item-2"
                className={cn(isEdit && 'accordion-page')}
                hideSeparator={isEdit}
              >
                <AccordionTrigger
                  disabled={!active}
                  fillForm={accessModified || currentReplayConfiguration?.accessModified}
                >
                  {t('replaySection.commands')}
                </AccordionTrigger>
                <AccordionContent className="space-y-4 ">
                  <div className="flex items-center justify-between">
                    <Label className={cn(!active && 'text-disable')}>
                      {t('replaySection.displayVideoCommands')}
                    </Label>
                    <Controller
                      name="displayVideoCommands"
                      control={form.control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!active}
                        />
                      )}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className={cn(!active && 'text-disable')}>
                      {t('replaySection.allowComments')}
                    </Label>
                    <Controller
                      name="allowComments"
                      control={form.control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!active}
                        />
                      )}
                    />
                  </div>
                  <Button
                    className="w-full mt-6 disabled:bg-gray-200 disabled:text-gray-500 "
                    onClick={handleSubmitAccess}
                    disabled={isSubmitting}
                  >
                    {isSubmitting && <Loader />}
                    {t('save')}
                  </Button>
                </AccordionContent>
              </AccordionItem>
            )}

            <AccordionItem
              value="item-3"
              className={cn(isEdit && 'accordion-page')}
              hideSeparator={isEdit}
            >
              <AccordionTrigger
                disabled={!active}
                fillForm={validityModified || currentReplayConfiguration?.validityModified}
              >
                {t('replaySection.validityDaysQuestion')}
              </AccordionTrigger>
              <AccordionContent className="space-y-4">
                {/* <Controller
                  name="limitDate"
                  control={form.control}
                  render={({ field }) => {
                    const selectedDate = field.value
                      ? DateTime.fromISO(field.value).toJSDate()
                      : undefined;

                    return (
                      <div className="space-y-2">
                        <Popover
                          open={active ? open : false}
                          onOpenChange={(isOpen) => {
                            if (active) {
                              setOpen(isOpen);
                            }
                          }}
                        >
                          <PopoverTrigger asChild>
                            <div
                              className={cn(
                                'w-full h-12 border text-tertiary border-input rounded-lg px-4 py-2 flex items-center justify-between',
                                active ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                                form.formState.errors.limitDate && '',
                              )}
                              onClick={(e) => {
                                if (!active) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }
                              }}
                            >
                              {field.value ? (
                                <>
                                  <div>
                                    <p className="text-sm">
                                      {t('replaySection.availabilityUntil')}
                                    </p>
                                    <p className="text-black">
                                      {DateTime.fromISO(field.value).toFormat('dd/MM/yyyy')}
                                    </p>
                                  </div>
                                  <CalendarDays color="black" size={24} />
                                </>
                              ) : (
                                <>
                                  <p className="text-base">
                                    {t('replaySection.availabilityUntil')}
                                  </p>
                                  <CalendarDays color="black" size={24} />
                                </>
                              )}
                            </div>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-(--radix-popover-trigger-width) overflow-hidden p-0 border-input"
                            align="start"
                          >
                            <Calendar
                              mode="single"
                              selected={selectedDate}
                              captionLayout="dropdown"
                              locale={fr}
                              className="w-full"
                              classNames={{
                                day: 'w-full ',
                              }}
                              disabled={(date) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const dateToCheck = new Date(date);
                                dateToCheck.setHours(0, 0, 0, 0);
                                return dateToCheck < today;
                              }}
                              onSelect={(date) => {
                                if (date && active) {
                                  field.onChange(DateTime.fromJSDate(date).toFormat('yyyy-MM-dd'));
                                  setOpen(false);
                                }
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                        {form.formState.errors.limitDate && (
                          <p className="text-sm text-red-500">
                            {form.formState.errors.limitDate.message}
                          </p>
                        )}
                      </div>
                    );
                  }}
                /> */}

                <div className="flex items-center justify-between">
                  <Label className={cn(!active && 'text-disable')}>
                    {t('replaySection.noDateLimit')}
                  </Label>
                  <Controller
                    name="noLimitDate"
                    control={form.control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!active}
                      />
                    )}
                  />
                </div>
                {!noLimitDate && (
                  <Controller
                    name="validityDays"
                    control={form.control}
                    render={({ field }) => (
                      <InputLabel
                        label={t('replaySection.validityDaysLabel')}
                        className="w-60"
                        inputMode="numeric"
                        // type="number"
                        min={0}
                        maxLength={3}
                        value={field.value.toString()}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            field.onChange(0);
                            return;
                          }
                          const numericValue = parseInt(value, 10);
                          if (!isNaN(numericValue) && numericValue >= 0) {
                            field.onChange(numericValue);
                          }
                        }}
                        disabled={!active}
                      />
                    )}
                  />
                )}
                <Button
                  className="w-full mt-6 disabled:bg-gray-200 disabled:text-gray-500 "
                  onClick={handleSubmitValidity}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Loader />}
                  {t('save')}
                </Button>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </TitleLayout>
    </div>
  );
}
